/**
 * HFN scorebug (HUD Kit A, i29QkH): gold network cap with the LIVE bug, red HOME team block (avatar, abbreviation,
 * possession football, drives-left pips), score box, blue AWAY team block, score box, and the game info block
 * (Drives: DRIVE n/N + the offense; Overtime: OT round; Classic: half + game clock).
 */
import { MatchFormat } from '@hf/shared';
import { type HudSnapshot } from '@hf/sim';
import { Head } from '../components';
import { characterIdAt, clock, halfLabel, teamLabel } from '@hf/client-core/ui-model/format';
import type { MatchSetup } from '@hf/client-core/ui-model/types';
import { driveOwner, drivesLeftChip } from '@hf/client-core/ui-model/hud';
export { driveOwner, drivesLeftChip } from '@hf/client-core/ui-model/hud';

const MAX_PIPS = 6;

export function Scorebug({ h, setup }: { h: HudSnapshot; setup: MatchSetup | null }) {
  const classic = (h.format ?? setup?.config.format) === MatchFormat.CLASSIC;
  const off = h.offense === 1 ? 1 : 0;
  const per = h.drivesPerPlayer;
  const drill = setup?.mode === 'practice' || setup?.mode === 'tutorial';
  const info = drill
    ? { top: setup?.mode === 'tutorial' ? 'FOOTBALL 101' : 'PRACTICE', big: '∞', chip: 'INFINITE DOWNS' }
    : h.overtime
      ? { top: 'OVERTIME', big: `OT${h.otRound > 1 ? ` ${h.otRound}` : ''}`, chip: 'BOTH GET A DRIVE' }
      : classic
        ? {
            top: halfLabel(h.half),
            big: clock(h.gameClockSec),
            chip: `${teamLabel(h.characters, setup, off)} BALL`,
          }
        : {
            top: driveOwner(setup, off, teamLabel(h.characters, setup, off)),
            big: `${Math.max(1, h.driveNumber[off])} OF ${per}`,
            chip: drivesLeftChip(h.drivesLeft[0] + h.drivesLeft[1]),
          };
  return (
    <div class="hf-scorebug" role="group" aria-label="Scoreboard">
      <div class="hf-scorebug__cap">
        <span class="t-display">HFN</span>
        <span class="hf-scorebug__live t-broadcast">
          <i aria-hidden="true" />
          LIVE
        </span>
      </div>
      {([0, 1] as const).map((side) => {
        const ch = characterIdAt(h.characters[side]);
        const left = h.drivesLeft[side];
        // Classic plays the clock, not a number of drives: no drive pips.
        const pips = drill || classic ? 0 : Math.min(MAX_PIPS, per);
        return [
          <div class={`hf-scorebug__team ${side === 0 ? 'home' : 'away'}`} key={`t${side}`}>
            <span class="hf-scorebug__avatar">
              <Head character={ch} class="hf-scorebug__head" flip={side === 1} />
            </span>
            <span class="hf-scorebug__abbr">
              <b class="t-broadcast">
                {teamLabel(h.characters, setup, side)}
                {h.offense === side ? <i class="hf-scorebug__ball" aria-label="possession" /> : null}
              </b>
              <span class="hf-scorebug__pips" aria-label={`${left} drives left`}>
                {Array.from({ length: pips }, (_, i) => (
                  <i key={i} class={i < Math.min(pips, left) ? 'on' : ''} />
                ))}
              </span>
            </span>
          </div>,
          <div
            class={`hf-scorebug__score t-broadcast ${side === 0 ? 'home' : 'away'}`}
            key={`s${side}-${h.score[side]}`}
          >
            {h.score[side]}
          </div>,
        ];
      })}
      <div
        class={`hf-scorebug__info ${classic && !drill && !h.overtime ? `clock ${h.clockRunning ? 'running' : 'stopped'} ${h.gameClockSec <= 10 ? 'late' : ''}` : ''}`}
      >
        <small>{info.top}</small>
        <b class="t-broadcast" aria-label={classic ? 'Game clock' : undefined}>
          {info.big}
        </b>
        <span class="hf-scorebug__chip t-broadcast">{info.chip}</span>
      </div>
    </div>
  );
}
